import type { DrawingBox, DrawingLabel, DrawingLine, OHLCV } from "@luxalgo/vela";
import type { ExecutionTradeOverlay } from "../../types/trade";
import {
  cohortPreviewDirectionColor,
  cohortPreviewMarkerShape,
  cohortPreviewOutcomeLabel,
  cohortPreviewOutcomeTextColor,
} from "../cohortTradesResolve";
import {
  isExecutionTradeLosing,
  SL_COLOR,
  TP_COLOR,
} from "../executionTradeOverlayLevels";
import { resolveEntryPlotOnCandles, snapToContainingCandleTime } from "../../views/Dashboard/TradeLog/components/TradeReview/executionCandlesEntryPlot";
import { executionCandleTimeToMillis } from "./executionCandlesToVelaBars";

/** Entry midline for the position tool (TradingView-style). */
const ENTRY_LINE_COLOR = "#7DD3E8";
const RISK_FILL = "rgba(224, 1, 1, 0.10)";
const REWARD_FILL = "rgba(6, 227, 99, 0.10)";
const RISK_FILL_DIM = "rgba(224, 1, 1, 0.06)";
const REWARD_FILL_DIM = "rgba(6, 227, 99, 0.06)";

type CohortBucket = {
  timeMs: number;
  winLong: number;
  winShort: number;
  lossLong: number;
  lossShort: number;
};

function emptyBucket(timeMs: number): CohortBucket {
  return {
    timeMs,
    winLong: 0,
    winShort: 0,
    lossLong: 0,
    lossShort: 0,
  };
}

function resolveOutcome(trade: ExecutionTradeOverlay): "win" | "loss" {
  if (trade.cohort_outcome === "loss") return "loss";
  if (trade.cohort_outcome === "win") return "win";
  return isExecutionTradeLosing(trade) ? "loss" : "win";
}

function barAtTime(bars: readonly OHLCV[], timeMs: number): OHLCV | null {
  if (!bars.length) return null;
  let lo = 0;
  let hi = bars.length - 1;
  let ans = bars[0]!;
  if (timeMs < bars[0]!.time) return bars[0]!;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const b = bars[mid]!;
    if (b.time <= timeMs) {
      ans = b;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

function medianBarLift(bars: readonly OHLCV[]): number {
  if (!bars.length) return 1;
  const ranges: number[] = [];
  const step = Math.max(1, Math.floor(bars.length / 400));
  for (let i = 0; i < bars.length; i += step) {
    const b = bars[i]!;
    const r = b.high - b.low;
    if (r > 0 && Number.isFinite(r)) ranges.push(r);
  }
  if (!ranges.length) {
    const b0 = bars[0]!;
    return Math.max(Math.abs(b0.close) * 0.001, 1e-6);
  }
  ranges.sort((a, b) => a - b);
  return ranges[Math.floor(ranges.length * 0.5)]!;
}

function shapeOnly(opts: {
  id: string;
  timeMs: number;
  price: number;
  style: DrawingLabel["style"];
  color: string;
  size: DrawingLabel["size"];
}): DrawingLabel {
  return {
    id: opts.id,
    paneId: "price",
    xloc: "bar_time",
    x: opts.timeMs,
    y: opts.price,
    yloc: "price",
    text: "",
    style: opts.style,
    color: opts.color,
    textColor: opts.color,
    size: opts.size,
    textAlign: "center",
    fontFamily: "default",
    overlay: true,
  };
}

function textOnly(opts: {
  id: string;
  timeMs: number;
  price: number;
  text: string;
  color: string;
  size: DrawingLabel["size"];
}): DrawingLabel {
  return {
    id: opts.id,
    paneId: "price",
    xloc: "bar_time",
    x: opts.timeMs,
    y: opts.price,
    yloc: "price",
    text: opts.text,
    // Plain text — `text_outline` strokes with contrastColor(fill), so bright
    // green wins get a heavy black halo while dark red losses barely show one.
    style: "none",
    color: opts.color,
    textColor: opts.color,
    size: opts.size,
    textAlign: "center",
    fontFamily: "default",
    overlay: true,
    noFill: true,
  };
}

/**
 * Overview markers: explicit price stacking so label order is stable at any zoom.
 * Longs:  text above arrow above candle.
 * Shorts: text below arrow below candle.
 * (Vela's abovebar/belowbar always draws same-label text *below* the shape, which
 * put long labels between the arrow and the candle — the opposite of what we want.)
 */
function pushOutcomeArrow(
  labels: DrawingLabel[],
  opts: {
    timeMs: number;
    direction: "long" | "short";
    outcome: "win" | "loss";
    count: number;
    bar: OHLCV | null;
    fallbackPx: number;
    stackIndex: number;
    dimmed: boolean;
    idPrefix: string;
    liftUnit: number;
  }
): void {
  if (opts.count <= 0) return;
  const shape =
    cohortPreviewMarkerShape(opts.direction) === "arrowUp" ? "arrowup" : "arrowdown";
  const high = opts.bar?.high ?? opts.fallbackPx;
  const low = opts.bar?.low ?? opts.fallbackPx;
  const local = Math.max(high - low, 0);
  const unit = Math.max(opts.liftUnit, Math.abs(opts.fallbackPx) * 0.0002, 1e-6);
  // Modest pads — enough clear air, capped so we don't blow the Y-scale again.
  const arrowGap = Math.min(
    Math.max(local * 0.45, unit * 0.85),
    unit * 2.2
  );
  const textGap = Math.min(
    Math.max(local * 0.4, unit * 1.05),
    unit * 2.4
  );
  const stackExtra = arrowGap * 0.95 * opts.stackIndex;

  let arrowY: number;
  let textY: number;
  if (opts.direction === "long") {
    arrowY = high + arrowGap + stackExtra;
    textY = arrowY + textGap; // text on top of the arrow
  } else {
    arrowY = low - arrowGap - stackExtra;
    textY = arrowY - textGap; // text below the arrow
  }

  const arrowColor = opts.dimmed
    ? opts.direction === "short"
      ? "rgba(224, 1, 1, 0.5)"
      : "rgba(6, 227, 99, 0.5)"
    : cohortPreviewDirectionColor(opts.direction);
  const labelColor = opts.dimmed
    ? opts.outcome === "loss"
      ? "rgba(224, 1, 1, 0.55)"
      : "rgba(6, 227, 99, 0.55)"
    : cohortPreviewOutcomeTextColor(opts.outcome);
  const label = cohortPreviewOutcomeLabel(opts.direction, opts.outcome, opts.count);

  labels.push(
    shapeOnly({
      id: `${opts.idPrefix}-${opts.direction}-${opts.outcome}-shape`,
      timeMs: opts.timeMs,
      price: arrowY,
      style: shape,
      color: arrowColor,
      size: "tiny",
    })
  );
  labels.push(
    textOnly({
      id: `${opts.idPrefix}-${opts.direction}-${opts.outcome}-text`,
      timeMs: opts.timeMs,
      price: textY,
      text: label,
      color: labelColor,
      size: "tiny",
    })
  );
}

function emitBucketLabels(
  labels: DrawingLabel[],
  bucket: CohortBucket,
  bars: readonly OHLCV[],
  dimmed: boolean,
  liftUnit: number
): void {
  const bar = barAtTime(bars, bucket.timeMs);
  const mid = bar != null ? (bar.high + bar.low) / 2 : 0;

  let longStack = 0;
  pushOutcomeArrow(labels, {
    timeMs: bucket.timeMs,
    direction: "long",
    outcome: "win",
    count: bucket.winLong,
    bar,
    fallbackPx: mid,
    stackIndex: longStack++,
    dimmed,
    idPrefix: `tessera-out-${bucket.timeMs}`,
    liftUnit,
  });
  pushOutcomeArrow(labels, {
    timeMs: bucket.timeMs,
    direction: "long",
    outcome: "loss",
    count: bucket.lossLong,
    bar,
    fallbackPx: mid,
    stackIndex: longStack++,
    dimmed,
    idPrefix: `tessera-out-${bucket.timeMs}`,
    liftUnit,
  });
  let shortStack = 0;
  pushOutcomeArrow(labels, {
    timeMs: bucket.timeMs,
    direction: "short",
    outcome: "win",
    count: bucket.winShort,
    bar,
    fallbackPx: mid,
    stackIndex: shortStack++,
    dimmed,
    idPrefix: `tessera-out-${bucket.timeMs}`,
    liftUnit,
  });
  pushOutcomeArrow(labels, {
    timeMs: bucket.timeMs,
    direction: "short",
    outcome: "loss",
    count: bucket.lossShort,
    bar,
    fallbackPx: mid,
    stackIndex: shortStack++,
    dimmed,
    idPrefix: `tessera-out-${bucket.timeMs}`,
    liftUnit,
  });
}

function formatPnlLabel(pnl: number): string {
  const abs = Math.abs(pnl);
  const body =
    abs >= 100 ? abs.toFixed(0) : abs >= 10 ? abs.toFixed(1) : abs.toFixed(2);
  return `${pnl >= 0 ? "" : "-"}${body}`;
}

function formatMoneyLabel(amount: number): string {
  const abs = Math.abs(amount);
  const body =
    abs >= 100 ? abs.toFixed(0) : abs >= 10 ? abs.toFixed(1) : abs.toFixed(2);
  return `$${body}`;
}

function formatSizeLabel(size: number): string {
  if (!Number.isFinite(size)) return "";
  const abs = Math.abs(size);
  if (abs >= 100) return abs.toFixed(0);
  if (abs >= 10) return abs.toFixed(2);
  return abs.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

function formatRrLabel(rr: number): string {
  if (!Number.isFinite(rr) || rr <= 0) return "";
  return rr >= 10 ? rr.toFixed(1) : rr.toFixed(2);
}

/** P&L % from entry → level for the trade direction (adverse = negative). */
function levelPnlPct(entry: number, level: number, isLong: boolean): number {
  if (!(entry > 0) || !Number.isFinite(level)) return 0;
  const raw = isLong ? (level - entry) / entry : (entry - level) / entry;
  return raw * 100;
}

function resolveRr(
  trade: ExecutionTradeOverlay,
  entry: number,
  sl: number | null,
  tp: number | null
): number | null {
  if (trade.rr != null && Number.isFinite(Number(trade.rr)) && Number(trade.rr) > 0) {
    return Number(trade.rr);
  }
  if (sl == null || tp == null || !Number.isFinite(sl) || !Number.isFinite(tp)) {
    return null;
  }
  const risk = Math.abs(entry - sl);
  const reward = Math.abs(tp - entry);
  if (!(risk > 0)) return null;
  return reward / risk;
}

function pushHzLine(
  lines: DrawingLine[],
  id: string,
  from: number,
  to: number,
  price: number,
  color: string,
  width = 1,
  style: DrawingLine["style"] = "solid",
  extend: DrawingLine["extend"] = "none"
): void {
  lines.push({
    id,
    paneId: "price",
    xloc: "bar_time",
    x1: from,
    y1: price,
    x2: to,
    y2: price,
    extend,
    color,
    invisible: false,
    width,
    style,
    arrowLeft: false,
    arrowRight: false,
    overlay: true,
  });
}

function pushZoneBox(
  boxes: DrawingBox[],
  opts: {
    id: string;
    left: number;
    right: number;
    a: number;
    b: number;
    fill: string;
  }
): void {
  boxes.push({
    id: opts.id,
    paneId: "price",
    xloc: "bar_time",
    left: opts.left,
    right: opts.right,
    top: Math.max(opts.a, opts.b),
    bottom: Math.min(opts.a, opts.b),
    extend: "none",
    bgColor: opts.fill,
    borderColor: undefined,
    borderWidth: 0,
    borderStyle: "solid",
    text: "",
    textColor: "#111827",
    textSize: "small",
    hAlign: "center",
    vAlign: "center",
    wrap: false,
    fontFamily: "default",
    bold: false,
    italic: false,
    overlay: true,
  });
}

function formatPriceLabel(price: number): string {
  const abs = Math.abs(price);
  if (abs >= 1000) return price.toFixed(2);
  if (abs >= 100) return price.toFixed(2);
  if (abs >= 1) return price.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
  return price.toFixed(5).replace(/0+$/, "").replace(/\.$/, "");
}

function formatAbsPct(pct: number): string {
  const abs = Math.abs(pct);
  return `${abs >= 10 ? abs.toFixed(1) : abs.toFixed(2)}%`;
}

function badgeLabel(opts: {
  id: string;
  timeMs: number;
  price: number;
  text: string;
  bg: string;
  size?: DrawingLabel["size"];
}): DrawingLabel {
  return {
    id: opts.id,
    paneId: "price",
    xloc: "bar_time",
    x: opts.timeMs,
    y: opts.price,
    yloc: "price",
    text: opts.text,
    style: "label_center",
    color: opts.bg,
    textColor: "#ffffff",
    size: opts.size ?? "small",
    textAlign: "center",
    fontFamily: "default",
    bold: true,
    overlay: true,
  };
}

export type PositionToolGeometry = {
  markTime: number;
  /** Latest horizon across zones (hit-testing). */
  endMs: number;
  slEndMs: number;
  tpEndMs: number;
  entryPx: number;
  sl: number | null;
  tp: number | null;
  top: number;
  bottom: number;
};

function barSpanEndMs(bars: readonly OHLCV[], barIndex: number): number {
  const bar = bars[barIndex];
  if (!bar) return 0;
  const next = bars[barIndex + 1];
  if (next) return next.time;
  const prev = bars[barIndex - 1];
  const step =
    prev != null ? Math.max(1, bar.time - prev.time) : 60 * 60 * 1000;
  return bar.time + step;
}

/** First bar (from entry) where SL or TP is touched — shared end for both zones. */
/**
 * The bars as candle-time points for the entry-plot helpers. OHLCV bars already carry `time`,
 * so no copy: copying ~50k bars per trade made every chart redraw take seconds.
 */
function candleTimesOf(bars: readonly OHLCV[]): Array<{ time: number }> {
  return bars as unknown as Array<{ time: number }>;
}

function firstSlOrTpTouchEndMs(
  bars: readonly OHLCV[],
  markTime: number,
  isLong: boolean,
  sl: number | null,
  tp: number | null,
  capMs: number
): number {
  if (!bars.length) return capMs;
  // Binary search: a backtest window can be ~50k bars and this runs per trade.
  let lo = 0;
  let hi = bars.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (bars[mid]!.time < markTime) lo = mid + 1;
    else hi = mid;
  }
  const startIdx = lo < bars.length ? lo : 0;
  for (let i = startIdx; i < bars.length; i += 1) {
    const b = bars[i]!;
    if (b.time > capMs) break;
    let hit = false;
    if (sl != null && Number.isFinite(sl)) {
      hit = isLong ? b.low <= sl : b.high >= sl;
    }
    if (!hit && tp != null && Number.isFinite(tp)) {
      hit = isLong ? b.high >= tp : b.low <= tp;
    }
    if (hit) return Math.min(barSpanEndMs(bars, i), capMs);
  }
  return capMs;
}

/** Resolve the selected-trade RR box time/price bounds (for hit-testing). */
export function resolvePositionToolGeometry(
  trade: ExecutionTradeOverlay,
  bars: readonly OHLCV[]
): PositionToolGeometry | null {
  const entryMs = executionCandleTimeToMillis(trade.entry_time);
  const exitMs = executionCandleTimeToMillis(
    trade.simulation_overlay === "changed" && trade.sim_exit_time
      ? trade.sim_exit_time
      : trade.exit_time
  );
  const entryPx = trade.entry_price != null ? Number(trade.entry_price) : null;
  if (entryMs == null || entryPx == null || !Number.isFinite(entryPx) || !bars.length) {
    return null;
  }
  const candleTimes = candleTimesOf(bars);
  const plotMs = resolveEntryPlotOnCandles(candleTimes, entryMs, exitMs);
  const markTime = plotMs?.time ?? entryMs;
  if (markTime == null) return null;

  let sl =
    trade.simulation_overlay === "changed" && trade.sim_sl_price != null
      ? Number(trade.sim_sl_price)
      : trade.sl_price != null
        ? Number(trade.sl_price)
        : null;
  let tp =
    trade.simulation_overlay === "changed" && trade.sim_tp_price != null
      ? Number(trade.sim_tp_price)
      : trade.tp_price != null
        ? Number(trade.tp_price)
        : null;
  if (sl != null && (!Number.isFinite(sl) || sl === entryPx)) sl = null;
  if (tp != null && (!Number.isFinite(tp) || tp === entryPx)) tp = null;

  const exitPx =
    trade.simulation_overlay === "changed" && trade.sim_exit_price != null
      ? Number(trade.sim_exit_price)
      : trade.exit_price != null
        ? Number(trade.exit_price)
        : null;
  const isLong = trade.direction !== "short";
  if (sl == null && tp == null && exitPx != null && Number.isFinite(exitPx) && exitPx !== entryPx) {
    const exitPct = levelPnlPct(entryPx, exitPx, isLong);
    if (exitPct >= 0) tp = exitPx;
    else sl = exitPx;
  }

  const barStep =
    bars.length >= 2 ? Math.max(1, bars[1]!.time - bars[0]!.time) : 60 * 60 * 1000;
  const chartEndMs = barSpanEndMs(bars, bars.length - 1);
  let capMs = chartEndMs;
  let exitKnown = false;
  if (exitMs != null && exitMs > markTime) {
    const snappedExit =
      snapToContainingCandleTime(candleTimes, exitMs) ?? exitMs;
    // Through the exit candle (the trade was open during it): a trade closed on the next
    // candle otherwise drew a one-candle sliver with its exit dot on the box edge.
    const exitIdx = bars.findIndex((b) => b.time === snappedExit);
    capMs = Math.min(capMs, exitIdx >= 0 ? barSpanEndMs(bars, exitIdx) : snappedExit);
    exitKnown = true;
  }
  if (!(capMs > markTime)) {
    capMs = markTime + Math.max(barStep, 1);
  }

  const hasLevel = sl != null || tp != null;
  // A closed trade's boxes run to its exit. Looking for the first touch of the stop / target is
  // only for a trade with no exit yet: the entry candle's own wick often reaches the stop level
  // (a sweep), which cut the boxes to one candle while the trade ran for hours.
  const zoneEndMs =
    hasLevel && !exitKnown
      ? firstSlOrTpTouchEndMs(bars, markTime, isLong, sl, tp, capMs)
      : capMs;
  const slEndMs = zoneEndMs;
  const tpEndMs = zoneEndMs;
  const endMs = Math.max(markTime + barStep, zoneEndMs);
  if (!(endMs > markTime)) return null;

  const top = Math.max(entryPx, sl ?? entryPx, tp ?? entryPx);
  const bottom = Math.min(entryPx, sl ?? entryPx, tp ?? entryPx);
  return { markTime, endMs, slEndMs, tpEndMs, entryPx, sl, tp, top, bottom };
}

/** A price the selected trade marks on the price axis (entry, and its exit). */
export type TradeAxisLevel = {
  /** `partial`: a piece closed before the end (quieter tag, with its share in `note`). */
  key: "entry" | "exit" | "partial";
  note?: string;
  price: number;
  color: string;
  /** Where its rail starts: the entry's or the exit's fill time. */
  timeMs?: number;
};

export const TRADE_EXIT_WIN_COLOR = TP_COLOR;
export const TRADE_EXIT_LOSS_COLOR = SL_COLOR;
export const TRADE_ENTRY_LINE_COLOR = ENTRY_LINE_COLOR;

/** The entry price, and the exit price once the trade closed (green won, red lost). */
export function tradeAxisLevels(trade: ExecutionTradeOverlay): TradeAxisLevel[] {
  const entry = trade.entry_price != null ? Number(trade.entry_price) : NaN;
  if (!Number.isFinite(entry)) return [];
  const changed = trade.simulation_overlay === "changed";
  // The entry's rail starts where the trade ends (its line already runs entry → exit).
  const endMs =
    executionCandleTimeToMillis(changed && trade.sim_exit_time ? trade.sim_exit_time : trade.exit_time) ??
    executionCandleTimeToMillis(trade.entry_time);
  const out: TradeAxisLevel[] = [
    { key: "entry", price: entry, color: ENTRY_LINE_COLOR, ...(endMs != null ? { timeMs: endMs } : {}) },
  ];
  // Closed in parts: `exit_price` is the average of the pieces — no fill happened there, so a
  // rail at it lined up with nothing. The rail marks the last piece instead (a real fill, on its dot).
  const legs = trade.strategy_rd_exit_legs ?? [];
  const lastLeg = legs.length > 1 ? legs[legs.length - 1]! : null;
  const exitRaw = lastLeg
    ? lastLeg.price
    : changed && trade.sim_exit_price != null
      ? trade.sim_exit_price
      : trade.exit_price;
  const exit = exitRaw != null ? Number(exitRaw) : NaN;
  const exitMs = executionCandleTimeToMillis(
    lastLeg ? lastLeg.time : changed && trade.sim_exit_time ? trade.sim_exit_time : trade.exit_time
  );
  // The pieces before the last: each a real fill, tagged with its share.
  legs.slice(0, -1).forEach((leg) => {
    if (legs.length < 2 || !Number.isFinite(leg.price)) return;
    const won = trade.direction === "short" ? leg.price < entry : leg.price > entry;
    const ms = executionCandleTimeToMillis(leg.time);
    out.push({
      key: "partial",
      price: leg.price,
      color: won ? TRADE_EXIT_WIN_COLOR : TRADE_EXIT_LOSS_COLOR,
      ...(leg.frac != null && Number.isFinite(leg.frac) ? { note: `${Math.round(leg.frac * 100)}%` } : {}),
      ...(ms != null ? { timeMs: ms } : {}),
    });
  });
  if (Number.isFinite(exit) && exit !== entry) {
    const won = trade.direction === "short" ? exit < entry : exit > entry;
    out.push({
      key: "exit",
      price: exit,
      color: won ? TRADE_EXIT_WIN_COLOR : TRADE_EXIT_LOSS_COLOR,
      ...(exitMs != null ? { timeMs: exitMs } : {}),
    });
  }
  return out;
}

/** True when crosshair is inside the selected trade's position tool. */
export function hitTestPositionTool(
  trade: ExecutionTradeOverlay,
  bars: readonly OHLCV[],
  crossTime: number,
  crossPrice: number | null
): boolean {
  if (crossPrice == null || !Number.isFinite(crossPrice)) return false;
  const g = resolvePositionToolGeometry(trade, bars);
  if (!g) return false;
  if (crossTime < g.markTime || crossTime > g.endMs) return false;
  return crossPrice <= g.top && crossPrice >= g.bottom;
}

function emitSelectedTradeDetail(
  labels: DrawingLabel[],
  lines: DrawingLine[],
  boxes: DrawingBox[],
  trade: ExecutionTradeOverlay,
  bars: readonly OHLCV[],
  showStats: boolean,
  opts?: { dimmed?: boolean }
): void {
  const dimmed = opts?.dimmed === true;
  const riskFill = dimmed ? RISK_FILL_DIM : RISK_FILL;
  const rewardFill = dimmed ? REWARD_FILL_DIM : REWARD_FILL;
  const g = resolvePositionToolGeometry(trade, bars);
  if (!g) return;
  const { markTime, endMs, slEndMs, tpEndMs, entryPx, sl, tp } = g;
  const isLong = trade.direction !== "short";
  const tid = trade.trade_id ?? "sel";
  const entryLineEndMs = endMs;
  const midX = markTime + (endMs - markTime) / 2;

  const exitAsStop =
    sl != null &&
    trade.sl_price == null &&
    trade.sim_sl_price == null &&
    trade.exit_price != null;
  const exitAsTarget =
    tp != null &&
    trade.tp_price == null &&
    trade.sim_tp_price == null &&
    trade.exit_price != null;

  const hasSl = sl != null;
  const hasTp = tp != null;

  const stepped = emitSteppedRisk(labels, lines, boxes, trade, bars, g, riskFill);
  if (hasSl && !stepped) {
    pushZoneBox(boxes, {
      id: `tessera-pos-risk-${tid}`,
      left: markTime,
      right: slEndMs,
      a: entryPx,
      b: sl!,
      fill: riskFill,
    });
    pushHzLine(lines, `tessera-pos-sl-edge-${tid}`, markTime, slEndMs, sl!, SL_COLOR, 1);
  }

  if (hasTp) {
    pushZoneBox(boxes, {
      id: `tessera-pos-reward-${tid}`,
      left: markTime,
      right: tpEndMs,
      a: entryPx,
      b: tp!,
      fill: rewardFill,
    });
    pushHzLine(lines, `tessera-pos-tp-edge-${tid}`, markTime, tpEndMs, tp!, TP_COLOR, 1);
  }

  pushHzLine(
    lines,
    `tessera-pos-entry-${tid}`,
    markTime,
    entryLineEndMs,
    entryPx,
    ENTRY_LINE_COLOR,
    1
  );
  // The dashed rails to the price axis and the price tags are drawn by tesseraAxisTags (a
  // renderer layer), not as drawings: a drawing extended right counts in the auto-scale, and an
  // old trade's price squashed today's candles.

  if (!showStats) return;

  // TV-style badges: Target (top) · center meta · Stop (bottom)
  const sizeRaw =
    trade.entry_quantity != null ? Number(trade.entry_quantity) : null;
  const sizeAbs =
    sizeRaw != null && Number.isFinite(sizeRaw) ? Math.abs(sizeRaw) : null;
  const rr = resolveRr(
    trade,
    entryPx,
    hasSl && !exitAsStop ? sl : null,
    hasTp && !exitAsTarget ? tp : null
  );
  const riskDollars =
    hasSl && sizeAbs != null && sizeAbs !== 0
      ? Math.abs(entryPx - sl!) * sizeAbs
      : null;
  const rewardDollars =
    hasTp && sizeAbs != null && sizeAbs !== 0
      ? Math.abs(tp! - entryPx) * sizeAbs
      : null;
  const realizedPnl =
    trade.simulation_overlay === "changed" && trade.sim_net_pnl != null
      ? Number(trade.sim_net_pnl)
      : trade.net_pnl != null
        ? Number(trade.net_pnl)
        : null;

  if (hasTp) {
    const targetPct = levelPnlPct(entryPx, tp!, isLong);
    const parts = [
      `${exitAsTarget ? "Exit" : "Target"}: ${formatPriceLabel(tp!)} (${formatAbsPct(targetPct)})`,
    ];
    if (rewardDollars != null && Number.isFinite(rewardDollars)) {
      parts.push(`Amount: ${formatMoneyLabel(rewardDollars).replace(/^\$/, "")}`);
    }
    labels.push(
      badgeLabel({
        id: `tessera-pos-badge-tp-${tid}`,
        timeMs: midX,
        price: tp!,
        text: parts.join("  "),
        bg: "#15803d",
      })
    );
  }

  if (hasSl) {
    const stopPct = levelPnlPct(entryPx, sl!, isLong);
    const parts = [
      `${exitAsStop ? "Exit" : "Stop"}: ${formatPriceLabel(sl!)} (${formatAbsPct(stopPct)})`,
    ];
    if (riskDollars != null && Number.isFinite(riskDollars)) {
      parts.push(`Amount: ${formatMoneyLabel(riskDollars).replace(/^\$/, "")}`);
    }
    labels.push(
      badgeLabel({
        id: `tessera-pos-badge-sl-${tid}`,
        timeMs: midX,
        price: sl!,
        text: parts.join("  "),
        bg: "#b91c1c",
      })
    );
  }

  const centerLines: string[] = [];
  const pnlBits: string[] = [];
  if (realizedPnl != null && Number.isFinite(realizedPnl)) {
    pnlBits.push(`Open PnL: ${formatPnlLabel(realizedPnl)}`);
  }
  if (sizeAbs != null && sizeAbs !== 0) {
    pnlBits.push(`Qty: ${formatSizeLabel(sizeAbs)}`);
  }
  if (pnlBits.length) centerLines.push(pnlBits.join(", "));
  const rrText = rr != null ? formatRrLabel(rr) : "";
  if (rrText) centerLines.push(`Risk/reward ratio: ${rrText}`);
  else centerLines.push(isLong ? "LONG" : "SHORT");

  labels.push(
    badgeLabel({
      id: `tessera-pos-badge-mid-${tid}`,
      timeMs: midX,
      price: entryPx,
      text: centerLines.join("\n"),
      bg: "#9f1239",
      size: "small",
    })
  );
}

function accumulateTrade(bucket: CohortBucket, trade: ExecutionTradeOverlay): void {
  const isShort = trade.direction === "short";
  const outcome = resolveOutcome(trade);
  if (isShort) {
    if (outcome === "loss") bucket.lossShort += 1;
    else bucket.winShort += 1;
  } else if (outcome === "loss") {
    bucket.lossLong += 1;
  } else {
    bucket.winLong += 1;
  }
}

/**
 * Cap overview markers without biasing to the newest trades only.
 * API order is often newest-first; a naive `.slice(0, N)` hides the left half of the chart.
 * When over the cap, stride-sample across entry time so markers span the full candle window.
 */
export function selectTradesForOverviewMarkers(
  trades: readonly ExecutionTradeOverlay[],
  selectedTradeId: string | null,
  limit = 2_500
): ExecutionTradeOverlay[] {
  if (!trades.length) return [];
  const byId = new Map<string, ExecutionTradeOverlay>();
  for (const t of trades) {
    if (t.trade_id) byId.set(t.trade_id, t);
  }

  const dated = trades
    .map((t) => ({ t, ms: executionCandleTimeToMillis(t.entry_time) }))
    .filter((x): x is { t: ExecutionTradeOverlay; ms: number } => x.ms != null)
    .sort((a, b) => a.ms - b.ms);

  let picked: ExecutionTradeOverlay[];
  if (dated.length <= limit) {
    picked = dated.map((x) => x.t);
  } else {
    picked = [];
    const seen = new Set<string>();
    const step = (dated.length - 1) / (limit - 1);
    for (let i = 0; i < limit; i += 1) {
      const idx = Math.min(dated.length - 1, Math.round(i * step));
      const t = dated[idx]!.t;
      const id = t.trade_id || `i-${idx}`;
      if (seen.has(id)) continue;
      seen.add(id);
      picked.push(t);
    }
  }

  if (selectedTradeId) {
    const sel = byId.get(selectedTradeId);
    if (sel && !picked.some((t) => t.trade_id === selectedTradeId)) {
      picked.push(sel);
    }
  }
  return picked;
}

/**
 * Overview = arrows + win/loss counts only (no entry dots).
 * Selected trade = RR boxes always; TV-style stats badges when `showPositionStats`.
 */
const FRAGILE_COLOR = "#f59e0b";
const ORDER_LINE_COLOR = "#64748b";
const EXIT_KIND_LABELS: Record<string, string> = { target: "Target", stop: "Stop", exit: "Exit" };
const STOP_MOVE_LABELS: Record<string, string> = { break_even: "Stop to break-even", trail: "Trailing stop" };

/**
 * A small tag pinned beside a point in pixels (not a price offset, which floated far off at
 * some zooms): `beside` = right of it, centred; `above` = above-right, just clear of a line.
 */
function pointTag(opts: {
  id: string;
  timeMs: number;
  price: number;
  text: string;
  color: string;
  place: "beside" | "above";
  /** Hover text: what happened here, in words. */
  tooltip?: string;
}): DrawingLabel {
  return {
    ...(opts.tooltip ? { tooltip: opts.tooltip } : {}),
    id: opts.id,
    paneId: "price",
    xloc: "bar_time",
    x: opts.timeMs,
    y: opts.price,
    yloc: "price",
    text: opts.text,
    style: opts.place === "beside" ? "label_left" : "label_lower_left",
    color: opts.color,
    textColor: opts.color,
    size: "small",
    textAlign: "left",
    fontFamily: "default",
    overlay: true,
    noFill: true,
  };
}

/**
 * The stop as it moved (Strategy Lab managed trades: break-even, trail). The red risk area
 * follows the stop in steps — from each move to the next, the last through the exit candle —
 * and is gone once the stop sits at or past the entry (nothing left to lose). The stop is the
 * area's solid edge, with a dotted riser at each move; the initial stop stays as a dashed line,
 * the 1R every result is measured against. Returns false when there is no path to draw.
 */
function emitSteppedRisk(
  labels: DrawingLabel[],
  lines: DrawingLine[],
  boxes: DrawingBox[],
  trade: ExecutionTradeOverlay,
  bars: readonly OHLCV[],
  g: PositionToolGeometry,
  riskFill: string
): boolean {
  const path = trade.strategy_rd_stop_path ?? [];
  if (path.length < 2 || !bars.length) return false;
  const tid = trade.trade_id ?? "sel";
  const isLong = trade.direction !== "short";
  const candleTimes = candleTimesOf(bars);
  const xs = path.map((step, i) => {
    if (i === 0) return g.markTime;
    const ms = executionCandleTimeToMillis(step.time);
    if (ms == null) return null;
    return Math.min(g.endMs, Math.max(g.markTime, snapToContainingCandleTime(candleTimes, ms) ?? ms));
  });
  const labelled = new Set<string>();
  const finalWhy = path[path.length - 1]?.why;
  const stoppedThere = (trade.strategy_rd_exit_reason ?? "").endsWith("stop") && trade.strategy_rd_exit_reason !== "Time stop";
  let prevPrice: number | null = null;
  path.forEach((step, i) => {
    const x = xs[i];
    if (x == null || !Number.isFinite(step.price)) return;
    const nextX = Math.max(x, xs.slice(i + 1).find((v): v is number => v != null) ?? g.endMs);
    // Still risking money while the stop is on the losing side of the entry.
    const atRisk = isLong ? step.price < g.entryPx : step.price > g.entryPx;
    if (atRisk && nextX > x) {
      pushZoneBox(boxes, { id: `tessera-pos-risk-${tid}-${i}`, left: x, right: nextX, a: g.entryPx, b: step.price, fill: riskFill });
    }
    if (prevPrice != null) {
      pushDiagLine(lines, `tessera-pos-stopmove-riser-${tid}-${i}`, x, prevPrice, x, step.price, SL_COLOR, "dotted");
    }
    if (nextX > x) pushHzLine(lines, `tessera-pos-stopmove-${tid}-${i}`, x, nextX, step.price, SL_COLOR, 1);
    prevPrice = step.price;
    const tag = STOP_MOVE_LABELS[step.why];
    if (i > 0 && tag && !labelled.has(step.why)) {
      labelled.add(step.why);
      labels.push(
        pointTag({
          id: `tessera-pos-stopmove-label-${tid}-${step.why}`,
          timeMs: x,
          price: step.price,
          text: tag,
          color: SL_COLOR,
          place: "above",
          tooltip:
            step.why === "trail"
              ? `From here the stop followed price (the red edge).${stoppedThere && finalWhy === "trail" ? " The rest of the trade closed on it." : ""}`
              : `The stop moved to ${formatPriceLabel(step.price)}, so the trade could no longer lose (the red area ends). ` +
                (stoppedThere && finalWhy === "break_even" ? "Price came back and closed the rest there." : "Price never came back to it."),
        })
      );
    }
  });
  // The initial stop: the 1R the result is measured in.
  pushHzLine(lines, `tessera-pos-sl-initial-${tid}`, g.markTime, g.endMs, Number(path[0]!.price), SL_COLOR, 1, "dashed");
  return true;
}

function pushDiagLine(
  lines: DrawingLine[],
  id: string,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: string,
  style: DrawingLine["style"] = "solid"
): void {
  lines.push({
    id,
    paneId: "price",
    xloc: "bar_time",
    x1,
    y1,
    x2,
    y2,
    extend: "none",
    color,
    invisible: false,
    width: 1,
    style,
    arrowLeft: false,
    arrowRight: false,
    overlay: true,
  });
}

/**
 * Strategy Lab position tools: an entry arrow on every trade; on the selected one a dot where
 * the trade actually exited (a gap
 * fills at the open, not at the stop / target), and a hollow ring on trades the
 * robustness test found fragile.
 */
function emitFillMarkers(
  labels: DrawingLabel[],
  lines: DrawingLine[],
  trade: ExecutionTradeOverlay,
  bars: readonly OHLCV[],
  isSelected = true
): void {
  const tid = trade.trade_id ?? "sel";
  const entryPx = trade.entry_price != null ? Number(trade.entry_price) : null;
  const candleTimes = candleTimesOf(bars);
  // Entry arrow: up under the candle for a long, down over it for a short.
  const entryMs = executionCandleTimeToMillis(trade.entry_time);
  if (entryMs != null && bars.length && (trade.direction === "long" || trade.direction === "short")) {
    const x = snapToContainingCandleTime(candleTimes, entryMs) ?? entryMs;
    const bar = barAtTime(bars, x);
    const unit = Math.max(medianBarLift(bars), Math.abs(entryPx ?? 0) * 0.0002, 1e-6);
    const isLong = trade.direction === "long";
    const y = isLong ? (bar?.low ?? entryPx ?? 0) - unit * 1.2 : (bar?.high ?? entryPx ?? 0) + unit * 1.2;
    labels.push(
      shapeOnly({
        id: `tessera-pos-entry-${tid}`,
        timeMs: x,
        price: y,
        style: isLong ? "arrowup" : "arrowdown",
        color: cohortPreviewDirectionColor(trade.direction),
        size: "tiny",
      })
    );
  }
  if (!isSelected) return; // exits and pending orders only on the trade you look at
  const pushExit = (id: string, time: string | null, px: number | null, tag?: string | null, tooltip?: string) => {
    const ms = executionCandleTimeToMillis(time);
    if (px == null || !Number.isFinite(px) || ms == null || !bars.length) return;
    const x = snapToContainingCandleTime(candleTimes, ms) ?? ms;
    const favorable =
      entryPx != null && (trade.direction === "short" ? px <= entryPx : px >= entryPx);
    const color = favorable ? TP_COLOR : SL_COLOR;
    labels.push(
      shapeOnly({
        id,
        timeMs: x,
        price: px,
        style: "circle",
        color,
        size: "tiny",
      })
    );
    if (tag) {
      // What closed it ("50%", "Target", "BE stop"…), right beside the dot.
      labels.push(pointTag({ id: `${id}-tag`, timeMs: x, price: px, text: tag, color, place: "beside", tooltip }));
    }
  };
  const legs = trade.strategy_rd_exit_legs ?? [];
  if (legs.length > 1) {
    // Closed in parts: one ✕ per piece; the average exit is not a real fill.
    legs.forEach((leg, i) => {
      const last = i === legs.length - 1;
      const pct = leg.frac != null && Number.isFinite(leg.frac) ? `${Math.round(leg.frac * 100)}%` : null;
      const at = formatPriceLabel(leg.price);
      // A partial says what it took; the last piece says what closed the rest.
      const tag = last
        ? trade.strategy_rd_exit_reason ?? EXIT_KIND_LABELS[leg.kind] ?? "Closed"
        : `${pct ?? "Part"} closed`;
      const tooltip = last
        ? `The remaining ${pct ?? "part"} closed here at ${at} (${(tag || "exit").toLowerCase()}).`
        : `${pct ?? "Part"} of the position closed here at ${at} to take profit; the rest stayed open.`;
      pushExit(`tessera-pos-exit-${tid}-${i}`, leg.time, leg.price, tag, tooltip);
    });
  } else {
    pushExit(
      `tessera-pos-exit-${tid}`,
      trade.exit_time,
      trade.exit_price != null ? Number(trade.exit_price) : null,
      trade.strategy_rd_exit_reason
    );
  }
  (trade.strategy_rd_order_lines ?? []).forEach((order, i) => {
    const from = executionCandleTimeToMillis(order.from);
    const to = executionCandleTimeToMillis(order.to);
    if (from == null || to == null || !Number.isFinite(order.price)) return;
    pushHzLine(
      lines,
      `tessera-pos-order-${tid}-${i}`,
      snapToContainingCandleTime(candleTimes, from) ?? from,
      snapToContainingCandleTime(candleTimes, to) ?? to,
      order.price,
      ORDER_LINE_COLOR,
      1,
      "dashed"
    );
  });
  if (trade.strategy_rd_fragile && entryPx != null && Number.isFinite(entryPx)) {
    const g = resolvePositionToolGeometry(trade, bars);
    if (g) {
      labels.push(
        textOnly({
          id: `tessera-pos-fragile-${tid}`,
          timeMs: g.markTime,
          price: entryPx,
          text: "◯",
          color: FRAGILE_COLOR,
          size: "normal",
        })
      );
    }
  }
}

export function emitTesseraTradeDrawings(
  trades: readonly ExecutionTradeOverlay[],
  bars: readonly OHLCV[],
  selectedTradeId: string | null,
  opts?: { showPositionStats?: boolean; allPositionTools?: boolean }
): { labels: DrawingLabel[]; lines: DrawingLine[]; boxes: DrawingBox[] } {
  const labels: DrawingLabel[] = [];
  const lines: DrawingLine[] = [];
  const boxes: DrawingBox[] = [];
  if (!trades.length) return { labels, lines, boxes };

  if (opts?.allPositionTools) {
    const selected =
      selectedTradeId != null
        ? trades.find((t) => t.trade_id === selectedTradeId) ?? null
        : null;
    for (const t of trades) {
      if (!t.trade_id) continue;
      const isSelected = selected != null && t.trade_id === selected.trade_id;
      // Every trade: its entry arrow. The stop / target box only on the trade you select —
      // zoomed out, one box per trade read as thin dashed slivers all over the chart.
      if (isSelected) {
        emitSelectedTradeDetail(labels, lines, boxes, t, bars, Boolean(opts.showPositionStats), { dimmed: false });
      }
      emitFillMarkers(labels, lines, t, bars, isSelected);
    }
    return { labels, lines, boxes };
  }

  const candleTimes = candleTimesOf(bars);
  const buckets = new Map<number, CohortBucket>();
  const list = selectTradesForOverviewMarkers(trades, selectedTradeId);
  const selected =
    selectedTradeId != null
      ? list.find((t) => t.trade_id === selectedTradeId) ??
        trades.find((t) => t.trade_id === selectedTradeId) ??
        null
      : list.length === 1
        ? list[0]!
        : null;

  for (const t of list) {
    if (selected && t.trade_id === selected.trade_id) continue;
    const entryMs = executionCandleTimeToMillis(t.entry_time);
    if (entryMs == null) continue;
    const exitMs = executionCandleTimeToMillis(
      t.simulation_overlay === "changed" && t.sim_exit_time
        ? t.sim_exit_time
        : t.exit_time
    );
    const plot = resolveEntryPlotOnCandles(candleTimes, entryMs, exitMs);
    if (!plot) continue;
    const key = plot.time;
    const prev = buckets.get(key) ?? emptyBucket(key);
    accumulateTrade(prev, t);
    buckets.set(key, prev);
  }

  const dimOverview = selected != null;
  const liftUnit = medianBarLift(bars);
  buckets.forEach((bucket) => {
    emitBucketLabels(labels, bucket, bars, dimOverview, liftUnit);
  });

  if (selected) {
    emitSelectedTradeDetail(
      labels,
      lines,
      boxes,
      selected,
      bars,
      Boolean(opts?.showPositionStats)
    );
  }

  return { labels, lines, boxes };
}

/**
 * Prefer clicking the trade arrow / label (above for longs, below for shorts).
 * Bar-body clicks still work as a fallback, but direction-matched arrow hits win.
 */
export function pickTradeIdFromChartCrosshair(
  trades: readonly ExecutionTradeOverlay[],
  bars: readonly OHLCV[],
  crossTime: number,
  crossPrice: number | null
): string | null {
  if (!trades.length || !bars.length) return null;
  const candleTimes = candleTimesOf(bars);
  const plot = resolveEntryPlotOnCandles(candleTimes, crossTime, crossTime);
  const barTime = plot?.time ?? null;
  const barIdx =
    barTime == null ? -1 : bars.findIndex((b) => b.time === barTime);
  const neighborTimes = new Set<number>();
  if (barIdx >= 0) {
    for (let d = -1; d <= 1; d += 1) {
      const b = bars[barIdx + d];
      if (b) neighborTimes.add(b.time);
    }
  }
  const liftUnit = medianBarLift(bars);
  // Match emit pads: arrow + text sit ~0.85–4.6 lift units off the wick.
  const arrowPad = liftUnit * 4.5;

  type Cand = {
    id: string;
    timeDist: number;
    priceDist: number;
    arrowHit: boolean;
    directionMatch: boolean;
  };
  const cands: Cand[] = [];
  for (const t of trades) {
    if (!t.trade_id || !t.entry_time) continue;
    const entryMs = executionCandleTimeToMillis(t.entry_time);
    if (entryMs == null) continue;
    const exitMs = executionCandleTimeToMillis(
      t.simulation_overlay === "changed" && t.sim_exit_time
        ? t.sim_exit_time
        : t.exit_time
    );
    const snapped = resolveEntryPlotOnCandles(candleTimes, entryMs, exitMs);
    if (!snapped || !neighborTimes.has(snapped.time)) continue;

    const entryPx = t.entry_price != null ? Number(t.entry_price) : null;
    const tradeBar = barAtTime(bars, snapped.time);
    const high = tradeBar?.high ?? entryPx;
    const low = tradeBar?.low ?? entryPx;
    if (high == null || low == null || !Number.isFinite(high) || !Number.isFinite(low)) {
      continue;
    }

    const isLong = t.direction !== "short";
    let arrowHit = false;
    let directionMatch = false;
    let inBody = false;

    if (crossPrice != null && Number.isFinite(crossPrice)) {
      inBody = crossPrice >= low && crossPrice <= high;
      const above = crossPrice > high && crossPrice <= high + arrowPad;
      const below = crossPrice < low && crossPrice >= low - arrowPad;
      arrowHit = above || below;
      directionMatch = (above && isLong) || (below && !isLong);
      // Must be on the candle, or in the long/short arrow corridor.
      if (!inBody && !arrowHit) continue;
      // Wrong-side arrow corridor (e.g. short when clicking above) — skip.
      if (arrowHit && !directionMatch && !inBody) continue;
    }

    const timeDist = Math.abs(entryMs - crossTime);
    const priceDist =
      crossPrice != null &&
      Number.isFinite(crossPrice) &&
      entryPx != null &&
      Number.isFinite(entryPx)
        ? Math.abs(entryPx - crossPrice)
        : Number.POSITIVE_INFINITY;

    cands.push({
      id: t.trade_id,
      timeDist,
      priceDist,
      arrowHit: arrowHit && directionMatch,
      directionMatch,
    });
  }
  if (!cands.length) return null;

  // Prefer a true arrow/label hit for that direction, then nearest in time.
  const arrowPool = cands.filter((c) => c.arrowHit);
  const pool = arrowPool.length ? arrowPool : cands;
  pool.sort((a, b) => {
    if (a.arrowHit !== b.arrowHit) return a.arrowHit ? -1 : 1;
    if (a.directionMatch !== b.directionMatch) return a.directionMatch ? -1 : 1;
    if (a.timeDist !== b.timeDist) return a.timeDist - b.timeDist;
    const pa = Number.isFinite(a.priceDist) ? a.priceDist : 1e15;
    const pb = Number.isFinite(b.priceDist) ? b.priceDist : 1e15;
    return pa - pb;
  });
  return pool[0]!.id;
}
